import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable } from 'rxjs';
import { RestRequestService } from '../restApi/rest-request.service';
import {
  REST_BASE,
  REST_COMMITMENTS,
  REST_PROJECTS,
  REST_TEMPORARY_SERVICE_PROVIDERS,
} from '../restApi/RestRoutes';
import { ErrorHandlerRest } from '../restApi/errorHandler-rest';
import {
  CommitmentsSummaryResponse,
  Commitment,
  DirectCostsSummaryResponse,
  ISidebarChangeOrder,
  ISidebarChangeOrderPayload,
  ISidebarCommitmentItem,
  ISidebarCommitmentItemsDTO,
  ISidebarContract,
  ISidebarContractPayload,
  ISidebarCost,
  ISidebarDirectCost,
  ISidebarDirectCostPayload,
  ISidebarInvoice,
  ISidebarInvoicePayload,
  TemporaryContractorResp,
} from '../store/commitments/commitments.types';
import { CurrentUserService } from './current-user.service';
import { DeepCopyService } from './deep-copy.service';
import { PrimeContractCommitmentSummary } from '@app/store/commitments/prime-contract-summary.commitments.types';
import { ContractSummaryDto } from '@app/store/commitments/contract-summary.commitments.types';
import { PcoContractWithChangeOrder } from '@app/store/pco/pco.types';

@Injectable({
  providedIn: 'root',
})
export class CommitmentsService extends ErrorHandlerRest {
  constructor(
    private rest: RestRequestService,
    protected user: CurrentUserService,
    protected router: Router,
  ) {
    super(user, router);
  }
  updateData = new EventEmitter();

  /** Gets all temporary contractors on the project
   *
   * TODO: Refactor when backend is finalized
   *
   */
  async getTemporaryContractors(projectId: number): Promise<TemporaryContractorResp[]> {
    try {
      return this.rest.get(`${REST_PROJECTS}/${projectId}/${REST_TEMPORARY_SERVICE_PROVIDERS}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAllCommitmentsSummary(projectId: number): Promise<CommitmentsSummaryResponse> {
    try {
      return this.rest.get(`${REST_PROJECTS}/${projectId}/${REST_COMMITMENTS}/summary`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDirectCostsSummary(projectId: number): Promise<DirectCostsSummaryResponse> {
    try {
      return this.rest.get(`${REST_PROJECTS}/${projectId}/direct-costs/summary`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getContractSummary(contractId: number): Promise<DirectCostsSummaryResponse> {
    try {
      return this.rest.get(`${REST_PROJECTS}/${REST_COMMITMENTS}/contracts/${contractId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getCommitmentById(id: number): Promise<Commitment> {
    try {
      return this.rest.get(`${REST_BASE}${REST_COMMITMENTS}/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getContractById(id: number): Promise<ContractSummaryDto> {
    try {
      return this.rest.get(`${REST_BASE}${REST_COMMITMENTS}/contracts/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Loads all contracts and all change orders within the given contract */
  getContractsWithChangeOrders(projectId: number) {
    return this.rest.getWithObservable<PcoContractWithChangeOrder[]>(
      `${REST_PROJECTS}/${projectId}/${REST_COMMITMENTS}/contract-change-orders`,
    );
  }

  getPrimeContractSummaryById(id: number): Observable<PrimeContractCommitmentSummary> {
    return this.rest
      .getWithObservable(`${REST_BASE}${REST_COMMITMENTS}/prime-contracts/${id}`)
      .pipe(
        catchError((error) => {
          throw this.handleError(error);
        }),
      );
  }

  addNewContract(contractForm: ISidebarContract) {
    const body: Partial<ISidebarContractPayload> = this.transformToContractDTO(contractForm);
    return this.addNewCommitment(body);
  }

  addNewChangeOrder(changeOrderData: ISidebarChangeOrder) {
    const body: Partial<ISidebarContractPayload> = this.transformToChangeOrderDTO(changeOrderData);
    return this.addNewCommitment(body);
  }

  addNewInvoice(invoiceData: ISidebarInvoice) {
    const body: Partial<ISidebarInvoicePayload> = this.transformToInvoiceDTO(invoiceData);
    return this.addNewCommitment(body);
  }

  addNewDirectCost(directCostForm: ISidebarDirectCost) {
    const body: Partial<ISidebarContractPayload> = this.transformToDirectCostDTO(directCostForm);
    return this.addNewCommitment(body);
  }

  addNewCommitment(body: Partial<ISidebarContractPayload> | Partial<ISidebarChangeOrderPayload>) {
    return new Promise<any>((res, rej) => {
      this.rest.post(`${REST_BASE}${REST_COMMITMENTS}`, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  modifyCommitment(body: Partial<ISidebarContractPayload> | Partial<ISidebarChangeOrderPayload>) {
    return new Promise<any>((res, rej) => {
      this.rest.put(`${REST_BASE}${REST_COMMITMENTS}/${body.id}`, body).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  modifyExistingContract(contractForm: ISidebarContract) {
    const body: Partial<ISidebarContractPayload> = this.transformToContractDTO(contractForm);

    return this.modifyCommitment(body);
  }

  modifyExistingChangeOrder(changeOrderData: ISidebarChangeOrder) {
    const body: Partial<ISidebarContractPayload> = this.transformToChangeOrderDTO(changeOrderData);
    return this.modifyCommitment(body);
  }

  modifyExistingInvoice(invoiceForm: ISidebarInvoice) {
    const body: Partial<ISidebarInvoicePayload> = this.transformToInvoiceDTO(invoiceForm);

    return this.modifyCommitment(body);
  }

  modifyExistingDirectCost(directCost: ISidebarDirectCost) {
    const body: Partial<ISidebarDirectCostPayload> = this.transformToDirectCostDTO(directCost);

    return this.modifyCommitment(body);
  }

  deleteCommitment(id: number) {
    return new Promise<any>((res, rej) => {
      this.rest.del(`${REST_BASE}${REST_COMMITMENTS}/${id}`).then(
        (dataRes) => {
          res(dataRes);
        },
        (err) => {
          rej(this.handleError(err));
        },
      );
    });
  }

  refreshData() {
    setTimeout(() => {
      this.updateData.emit(true);
    }, 500);
  }

  /**
   * Add common fields to DTO, which are used in all commitment types
   * @param commitmentData
   */
  commonDTOFields(
    commitmentData: ISidebarContract | ISidebarChangeOrder | ISidebarInvoice | ISidebarDirectCost,
  ) {
    const body:
      | Partial<ISidebarContractPayload>
      | Partial<ISidebarChangeOrderPayload>
      | Partial<ISidebarInvoicePayload> = {};
    if (commitmentData.id) {
      body.id = commitmentData.id;
    }
    if (commitmentData.service_provider?.temporary_service_provider_id) {
      body.temporary_service_provider_id =
        commitmentData.service_provider.temporary_service_provider_id;
    }
    if (commitmentData.service_provider?.service_provider_user_id) {
      body.service_provider_user_id = commitmentData.service_provider.service_provider_user_id;
    }
    if (
      commitmentData.service_provider?.name &&
      !commitmentData.service_provider?.service_provider_user_id &&
      !commitmentData.service_provider?.temporary_service_provider_id
    ) {
      body.tmp_sp_name = commitmentData.service_provider.name;
    }

    return body;
  }

  transformToContractDTO(contractForm: ISidebarContract) {
    const body: ISidebarContractPayload = {
      project_id: contractForm.project_id,
      type: contractForm.type,
      title: contractForm.title,
      description: contractForm.description,
      added_at: contractForm.added_at,
      approval_status: contractForm.approval_status,
      commitment_items: [
        ...this.transformCommitmentItems(contractForm.commitment_items),
        ...this.transformCommitmentItems(contractForm?.deleted_commitment_items),
      ],
    };

    if (!body.description) {
      delete body.description;
    }

    return {
      ...body,
      ...this.commonDTOFields(contractForm),
    };
  }

  transformToChangeOrderDTO(changeOrderData: ISidebarChangeOrder) {
    const body: ISidebarChangeOrderPayload = {
      project_id: changeOrderData.project_id,
      type: changeOrderData.type,
      change_order_type: changeOrderData.change_order_type,
      title: changeOrderData.title,
      description: changeOrderData.description,
      added_at: changeOrderData.added_at,
      approval_status: changeOrderData.approval_status,
      contract_id: changeOrderData.contract_id,
      commitment_items: [
        ...this.transformCommitmentItems(changeOrderData.commitment_items),
        ...this.transformCommitmentItems(changeOrderData.deleted_commitment_items),
      ],
    };

    if (!body.description) {
      delete body.description;
    }

    return {
      ...body,
      ...this.commonDTOFields(changeOrderData),
    };
  }

  transformToInvoiceDTO(invoiceData: ISidebarInvoice) {
    const body: ISidebarInvoicePayload = {
      project_id: invoiceData.project_id,
      type: invoiceData.type,
      invoice_number: invoiceData?.invoice_number ?? invoiceData?.title,
      description: invoiceData.description,
      added_at: invoiceData.added_at,
      paid_date: invoiceData.paid_date,
      paid_status: invoiceData.paid_status,
      contract_id: invoiceData.contract_id,
      costs: [
        ...this.transformCommitmentCosts(invoiceData.costs),
        ...this.transformCommitmentCosts(invoiceData.deleted_costs),
      ],
    };

    if (!body.paid_status) {
      delete body.paid_date;
    }

    if (!body.description) {
      delete body.description;
    }

    return {
      ...body,
      ...this.commonDTOFields(invoiceData),
    };
  }

  transformToDirectCostDTO(directCost: ISidebarDirectCost) {
    const body: ISidebarDirectCostPayload = {
      project_id: directCost.project_id,
      vendor: directCost.vendor,
      type: directCost.type,
      title: directCost.title,
      description: directCost.description,
      added_at: directCost.added_at,
      paid_date: directCost.paid_date,
      paid_status: directCost.paid_status,
      commitment_items: [
        ...this.transformCommitmentItems(directCost.commitment_items),
        ...this.transformCommitmentItems(directCost.deleted_commitment_items),
      ],
    };

    if (!body.paid_status) {
      delete body.paid_date;
    }

    if (!body.description) {
      delete body.description;
    }

    return {
      ...body,
      ...this.commonDTOFields(directCost),
    };
  }

  transformCommitmentItems(commitmentItems: Partial<ISidebarCommitmentItem>[]) {
    if (!commitmentItems?.length) {
      return [];
    }
    return commitmentItems.map((commitmentItem: ISidebarCommitmentItem) => {
      const transformed: ISidebarCommitmentItemsDTO = {
        item_id: commitmentItem.item.id,
        start_date: commitmentItem?.start_date ?? null,
        duration: commitmentItem.duration ?? null,
        budget_tag_id: commitmentItem.budget_tag.id,
        budget_tag_name: commitmentItem.budget_tag.name,
        costs: [...commitmentItem.costs, ...(commitmentItem?.deleted_costs ?? [])],
        deleted: !!commitmentItem?.deleted,
      };

      if (!commitmentItem.start_date) {
        delete transformed.start_date;
      }

      if (commitmentItem.duration === null) {
        delete transformed.duration;
      }

      if (commitmentItem.id) {
        transformed.id = commitmentItem.id;
      }
      return transformed;
    });
  }

  transformCommitmentCosts = (costs: ISidebarCost[]) => {
    const transformedCosts: ISidebarCost[] = DeepCopyService.deepCopy(costs);
    if (!transformedCosts?.length) {
      return [];
    }
    return transformedCosts.map((cost) => {
      const transformed: ISidebarCost = {
        id: cost.id ?? null,
        name: cost.name,
        value: cost.value,
        parent_id: cost.parent_id,
        deleted: cost?.deleted ?? false,
      };
      if (!cost.id) {
        delete transformed.id;
      }
      return transformed;
    });
  };
}
